import React from 'react';
import { KPI } from '../types';
import { Activity, Users, AlertTriangle, DollarSign, Briefcase, Wrench } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const kpiData: KPI[] = [
  { label: 'Revenue (MTD)', value: 'R 1.2M', trend: 'up', trendValue: '+12%', color: 'text-green-500' },
  { label: 'Open Jobs', value: '34', trend: 'down', trendValue: '-5', color: 'text-titan-500' },
  { label: 'Safety Incidents', value: '0', trend: 'neutral', trendValue: '0', color: 'text-green-500' },
  { label: 'Pipeline Value', value: 'R 4.5M', trend: 'up', trendValue: '+8%', color: 'text-blue-500' },
];

const revenueData = [
  { name: 'Jan', sales: 4000, service: 2400 },
  { name: 'Feb', sales: 3000, service: 1398 },
  { name: 'Mar', sales: 2000, service: 9800 },
  { name: 'Apr', sales: 2780, service: 3908 },
  { name: 'May', sales: 1890, service: 4800 },
  { name: 'Jun', sales: 2390, service: 3800 },
];

export const Dashboard: React.FC = () => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center mb-6 min-h-[4rem]">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Command Center</h1>
          <p className="text-slate-400 mt-1">Titan Ecosystem Overview</p>
        </div>
        <div className="text-right">
           <div className="text-sm text-slate-400">System Status</div>
           <div className="flex items-center gap-2 text-green-500 text-sm font-bold">
             <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
             OPERATIONAL
           </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiData.map((kpi, idx) => (
          <div key={idx} className="bg-slate-800 p-6 rounded-lg border border-slate-700 shadow-sm hover:border-titan-500/50 transition">
            <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">{kpi.label}</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">{kpi.value}</span>
              <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-400' : kpi.trend === 'down' ? 'text-red-400' : 'text-slate-500'}`}>
                {kpi.trendValue}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-slate-800 p-6 rounded-lg border border-slate-700">
          <h3 className="text-lg font-bold text-white mb-6">Revenue Performance</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#cd8832" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#cd8832" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorService" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#475569', color: '#f1f5f9' }} />
                <Area type="monotone" dataKey="sales" stroke="#cd8832" fillOpacity={1} fill="url(#colorSales)" />
                <Area type="monotone" dataKey="service" stroke="#3b82f6" fillOpacity={1} fill="url(#colorService)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts & Notifications */}
        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
          <h3 className="text-lg font-bold text-white mb-4">Critical Alerts</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-red-900/20 border border-red-900/50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-red-400 font-semibold text-sm">HSSE Incident Reported</div>
                <div className="text-red-300/70 text-xs">Site: Rustenburg Shaft 2 • Near Miss</div>
              </div>
            </div>
             <div className="flex items-start gap-3 p-3 bg-orange-900/20 border border-orange-900/50 rounded-lg">
              <Wrench className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-orange-400 font-semibold text-sm">Asset Overdue Service</div>
                <div className="text-orange-300/70 text-xs">Compressor CMP-102 • -48 hours</div>
              </div>
            </div>
             <div className="flex items-start gap-3 p-3 bg-blue-900/20 border border-blue-900/50 rounded-lg">
              <Briefcase className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-blue-400 font-semibold text-sm">Quote Approval Needed</div>
                <div className="text-blue-300/70 text-xs">Anglo American • R 450,000</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Users, label: 'Technician Map', color: 'bg-blue-600' },
          { icon: Activity, label: 'Live Telemetry', color: 'bg-green-600' },
          { icon: DollarSign, label: 'Create Invoice', color: 'bg-slate-700' },
          { icon: Wrench, label: 'Log Breakdown', color: 'bg-red-600' },
        ].map((action, i) => (
          <button key={i} className={`${action.color} p-4 rounded-lg flex items-center justify-center gap-2 text-white font-semibold hover:opacity-90 transition shadow-lg`}>
            <action.icon className="w-5 h-5" />
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};